/**
 * Key-message highlight index for the inline sentence highlights inside
 * BRAND / PRODUCT AI Visibility Reports.
 *
 * The matcher (see `@repo/core` `attribute-match.ts`) stores top-5 matched
 * sentence snippets per attribute on `AiReport.trackedAttributeResults`. This
 * util flattens that JSON into a fast lookup keyed by `(platform, normalized
 * sentence)` so the AiVisibilitySection can highlight matched sentences in
 * the rendered response text without re-running the matcher in the browser.
 *
 * Inline highlights are intentionally a *teaser* — the comprehensive view
 * lives on the historical metrics dashboard's awareness segment. Sentences
 * below the top-5 cutoff per attribute won't highlight; that is by design.
 */

export type HighlightSentiment = "POSITIVE" | "NEGATIVE" | "NEUTRAL";

export interface HighlightEntry {
  keyMessageId: string;
  message: string;
  sentiment: HighlightSentiment;
  score: number;
}

export type HighlightIndex = Map<string, HighlightEntry>;

/**
 * Normalize a sentence for the (platform, sentence) → entry lookup.
 *
 * The matcher's snippet text is whatever `sentenceSplit` grabbed from the
 * **raw markdown** AI response, so it can include leading list markers
 * (`- `, `* `, `1. `), heading hashes (`## `), bold/italic markers
 * (`**...**`, `*...*`), and inline code backticks. The HTML post-processor
 * sees the **rendered** HTML, where the markdown parser has stripped those
 * markers and converted them to tags (`<li>`, `<strong>`, etc.). Without
 * stripping the markdown syntax from the snippet first, the two sides would
 * never agree.
 *
 * Order matters: strip block-level markers first (leading bullets / headings),
 * then inline emphasis, then collapse whitespace, then trim terminal punct.
 *
 * The normalization MUST match between the index builder and the HTML
 * post-processor; if you change one, change the other.
 */
export function normalizeSentenceForHighlight(s: string): string {
  return (
    s
      // Strip leading bullet markers: "- foo", "* foo", "+ foo", "1. foo"
      .replace(/^\s*(?:[-*+]|\d+\.)\s+/u, "")
      // Strip leading heading markers: "# foo", "## foo", ...
      .replace(/^\s*#+\s+/u, "")
      // Strip inline bold: **foo** -> foo
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      // Strip inline italic: *foo* -> foo (run after bold so ** doesn't get partially eaten)
      .replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, "$1$2")
      // Strip inline code: `foo` -> foo
      .replace(/`([^`]+)`/g, "$1")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[.!?,;:]+$/u, "")
      .trim()
  );
}

function keyFor(platform: string, sentence: string): string {
  return `${platform.toLowerCase()}::${normalizeSentenceForHighlight(sentence)}`;
}

/**
 * Sentiment priority for conflict resolution when the same sentence matches
 * multiple key messages. NEGATIVE wins — a negative narrative being repeated
 * is more urgent to surface than a positive one. NEUTRAL is last.
 */
const SENTIMENT_PRIORITY: Record<HighlightSentiment, number> = {
  NEGATIVE: 2,
  POSITIVE: 1,
  NEUTRAL: 0,
};

function shouldReplace(
  existing: HighlightEntry,
  candidate: HighlightEntry,
): boolean {
  const ep = SENTIMENT_PRIORITY[existing.sentiment];
  const cp = SENTIMENT_PRIORITY[candidate.sentiment];
  if (cp !== ep) return cp > ep;
  return candidate.score > existing.score;
}

interface RawAttributeSnippet {
  platform: string;
  sentence: string;
  score: number;
}

interface RawAttributeResult {
  keyMessageId: string;
  message: string;
  sentiment: HighlightSentiment;
  snippets?: RawAttributeSnippet[];
}

interface RawTrackedAttributeResults {
  results?: RawAttributeResult[];
}

/**
 * Build the highlight index from `AiReport.trackedAttributeResults` (the JSON
 * snapshot persisted at report finalize time).
 *
 * Returns an empty map for legacy reports without the snapshot, or for
 * reports with no key messages. The HTML post-processor short-circuits on
 * an empty map so callers can pass it through unconditionally.
 */
export function buildKeyMessageHighlightIndex(
  trackedAttributeResults: unknown,
): HighlightIndex {
  const index: HighlightIndex = new Map();
  const data = trackedAttributeResults as RawTrackedAttributeResults | null;
  const results = data?.results;
  if (!Array.isArray(results)) return index;

  for (const result of results) {
    if (!Array.isArray(result.snippets)) continue;
    for (const snippet of result.snippets) {
      if (!snippet.platform || !snippet.sentence) continue;
      const key = keyFor(snippet.platform, snippet.sentence);
      if (!key) continue;
      const candidate: HighlightEntry = {
        keyMessageId: result.keyMessageId,
        message: result.message,
        sentiment: result.sentiment,
        score: snippet.score,
      };
      const existing = index.get(key);
      if (!existing || shouldReplace(existing, candidate)) {
        index.set(key, candidate);
      }
    }
  }

  return index;
}

/** Look up an entry by platform + sentence. */
export function lookupHighlight(
  index: HighlightIndex,
  platform: string,
  sentence: string,
): HighlightEntry | undefined {
  return index.get(keyFor(platform, sentence));
}
